import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import usePerformanceActionsApi, { nextStatuses } from '../../performance-actions/config';
import { Badge as InsightBadge } from '../../performance-insights/config';
import { parseTableParams } from '../../../utils/Utils';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import theme from '../../../assets/styles/theme';
import { changeActionStatus } from '../config';

// P7a — E15 alt alan: aksiyon geçmişi (sorumlu, son tarih, bağlı nesne, durum ve SONUÇ). Kaynak bulgunun kanıtı incelemeden sonra
// değiştiyse "Eski Bulgu" etiketi. Tamamlarken isteğe bağlı sonuç notu (insan geri bildirimi) çekmecede alınır. Görünürlük
// sunucuda: İK hepsi; yönetici kendi sorumlu olduğu + gördüğü bulgulardan açılmış aksiyonlar. Aksiyonlar sayfasının veri kancası
// ve kolonları yeniden kullanılır; sonuç/bağlı nesne/eski bulgu kolonları sona eklenir (süzgeç indeksleri kaymaz).
const STATUS_ACTION = {
  planned: { key: 'reopen', type: 'return_to_draft' },
  in_progress: { key: 'start', type: 'resume' },
  done: { key: 'complete', type: 'complete' },
  cancelled: { key: 'cancel', type: 'cancel' },
};

export default function ActionsTab({ periodId, containerHeight, refreshKey }) {
  const { t } = useTranslation();
  const scope = useMemo(() => (periodId ? { 'periodId[eq]': periodId } : {}), [periodId]);
  const [queryParams, setQueryParams] = useState(() => ({ pageSize: 10, pageNumber: 1, ...scope }));
  const [filterList, setFilterList] = useState([]);
  const [completing, setCompleting] = useState(null);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    setQueryParams((q) => {
      const next = { ...q, pageNumber: 1 };
      delete next['periodId[eq]'];
      return { ...next, ...scope };
    });
  }, [scope]);

  const {
    fetching, data, pagination, columns: baseColumns, refresh,
  } = usePerformanceActionsApi(queryParams);

  useEffect(() => { if (refreshKey) refresh(); }, [refreshKey, refresh]);

  const handleTableChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams({ ...parseTableParams(tableState, baseColumns), ...scope });
  }, [baseColumns, scope]);

  const setStatus = useCallback(async (row, status, outcomeNote) => {
    const ok = await changeActionStatus(row.id, status, outcomeNote);
    if (ok) refresh();
    return ok;
  }, [refresh]);

  const buildRowActions = useCallback((row) => nextStatuses(row, !!row.canManage).map((s) => ({
    type: STATUS_ACTION[s].type,
    label: t(`performance.action.rowAction.${STATUS_ACTION[s].key}`),
    onClick: () => (s === 'done' ? setCompleting(row) : setStatus(row, s)),
  })), [t, setStatus]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'linkedScopeName',
      label: t('performance.aiCenter.action.linked'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'outcomeNote',
      label: t('performance.aiCenter.action.outcome'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'sourceEvidenceChanged',
      label: t('performance.aiCenter.action.sourceState'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row?.sourceInsightId) return '-';
          return (
            <>
              {t(`performance.insight.statusValue.${row.sourceInsightStatus}`, { defaultValue: row.sourceInsightStatus || '-' })}
              {v ? <> <InsightBadge color={theme.colors.auxiliar.danger} bg={theme.colors.bgColor}>{t('performance.insight.staleBadge')}</InsightBadge></> : null}
            </>
          );
        },
      },
    },
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          const actions = buildRowActions(row);
          return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
        },
      },
    },
  ], [baseColumns, data, t, buildRowActions]);

  const outcomeSchema = useMemo(() => [
    { name: 'outcomeNote', label: t('performance.aiCenter.action.outcome'), type: 'textarea', maxLength: 1000, rows: 4 },
  ], [t]);

  return (
    <div style={{ height: '100%', minHeight: 0, overflow: 'hidden' }} data-tab="actions">
      <GenericFormDrawer
        open={!!completing}
        onClose={() => setCompleting(null)}
        title={t('performance.aiCenter.action.completeTitle')}
        schema={outcomeSchema}
        submitLabel={t('performance.action.rowAction.complete')}
        onSave={async (form) => {
          const ok = await setStatus(completing, 'done', (form.outcomeNote || '').trim() || undefined);
          if (ok) setCompleting(null);
          return ok;
        }}
      />
      <DataTable
        title={t('performance.aiCenter.actionsTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleTableChange}
        canCreateNewRecord={false}
        tableBodyMaxHeight={containerHeight ? `${Math.max(containerHeight - 144, 200)}px` : undefined}
      />
    </div>
  );
}
